// @ds/forms/CrudModalShell — chrome layer for create/edit/duplicate flows.
//
// Owns the modal open/close lifecycle and renders title + body + footer
// with mode-derived defaults (Polish entity vocabulary). Wraps
// `@ds/primitives/Modal` so all the AntD foot-guns (`destroyOnHidden`,
// `bodyStyle`/`styles.body`, declarative actions footer) stay locked
// down. The body is provided by the consumer — `@ds/forms/ModalForm` is
// the typical inhabitant.
//
// AntD wrap justification (Rule 5):
//   1. Foot-gun: every CRUD modal in the codebase hand-rolls the same
//      title pattern ("Nowy X" / "Edytuj X" / "Duplikuj X"), the same
//      Cancel + Save buttons, and the same Polish save labels ("Utwórz"
//      / "Zapisz" / "Utwórz kopię"). One primitive, one source of truth.
//   2. ConfigProvider override: not needed — chrome composes Modal which
//      already absorbs the editorial AntD theme.
//   3. Inputs/Outputs/Actions surface: a single `mode` prop drives the
//      title, save label, and orchestrator behaviour. Pays for itself
//      against six hand-wired AntD Modal props per call site.
//
// Auto-emitted testids (root `crud-modal` overridable via `testId`):
//   - `modal-action-save`    — Save button (from @ds/primitives/Modal)
//   - `modal-action-cancel`  — Cancel button (from @ds/primitives/Modal)
//   - `modal-close`          — × close button (from @ds/primitives/Modal)
//   - `crud-modal-error`     — error banner row (when `error` is set)
//
// formTestIdPrefix — testid injection for raw AntD Form bodies:
//   When `formTestIdPrefix` is set the body children are walked and every
//   `Form.Item` that has a `name` prop has its single direct-child element
//   cloned with `data-testid="${prefix}-${fieldName}"`.  The walk is
//   recursive so Form.Items nested inside wrapper divs / Row / Col are found.
//   Skip rules:
//     - Inner child already has `data-testid` → not overwritten.
//     - Form.Item has no `name` (decorative / shouldUpdate) → left alone.
//     - Inner child is not a single React element → left alone.
//   The body is also wrapped in `FormFieldTestIdContext.Provider` so any
//   @ds field primitives nested deeper receive the same prefix automatically.

import { Children, cloneElement, isValidElement } from "react";
import type { ReactNode } from "react";
import { Form } from "antd";

import { Modal } from "@ds/primitives/Modal";
import { FormFieldTestIdContext } from "@ds/forms/formTestIdContext";
import { Box } from "@ds/layout/Box";
import { TextValue } from "@ds/display/TextValue";
import { defaultCrudTitle, defaultCrudSaveLabel, type CrudMode } from "@ds/forms/CrudModalShell.helpers";

export type { CrudMode } from "@ds/forms/CrudModalShell.helpers";

// ─── Inputs ──────────────────────────────────────────────────────────────

export interface CrudModalShellInput {
  /** Open state — controlled by the consumer. */
  open: boolean;
  /** Mode drives title + Save label defaults. */
  mode: CrudMode;
  /** Entity name singular, e.g. "produkt", "faktura". Used in default titles. */
  entityName: string;
  /** Optional explicit title — overrides the mode-derived default. */
  title?: ReactNode;
  /** Loading state — disables Save (e.g. while initial entity loads). */
  loading?: boolean;
  /** Saving state — disables Cancel + spins the Save button. */
  saving?: boolean;
  /** Centralised error banner above the body. */
  error?: string;
  /** Body content — typically `<ModalForm>` or a custom form. */
  children: ReactNode;
  /** Width override (px). Default 720 (wide form). */
  width?: number;
  /** Optional override for the Save button label. Falls back to `defaultCrudSaveLabel(mode)`. */
  saveLabel?: ReactNode;
  /** Optional override for the Cancel button label. Default "Anuluj". */
  cancelLabel?: ReactNode;
  /** Disable the Save button externally (e.g. validation gate). */
  saveDisabled?: boolean;
  /** Optional `data-testid` applied to the modal root. Default `crud-modal`. */
  testId?: string;
  /**
   * When set, walks the body children and injects
   * `data-testid="${prefix}-${name}"` on the direct-child element of every
   * `Form.Item` that carries a `name` prop. The body is also wrapped in
   * `FormFieldTestIdContext.Provider` so any @ds field primitives nested
   * deeper receive the same prefix automatically.
   *
   * Zero consumer changes inside the form body are required — they just
   * gain this single prop on `CrudModalShell`.
   */
  formTestIdPrefix?: string;
}

// ─── Outputs ─────────────────────────────────────────────────────────────

export interface CrudModalShellOutput {
  /** Fires when the modal wants to close (× / Esc / Cancel). */
  onClose: () => void;
  /** Fires when the user clicks Save. */
  onSubmit: () => void | Promise<void>;
}

// No side actions — the chrome only owns Save + Cancel.

export type CrudModalShellProps = CrudModalShellInput & CrudModalShellOutput;

// ─── Form.Item testid walker ──────────────────────────────────────────────
//
// Walks `node` recursively. For each AntD Form.Item that has a `name` prop
// and whose single direct child is a plain React element without an existing
// `data-testid`, clones the child adding the derived testid. All other nodes
// are passed through unchanged.

function injectFormItemTestIds(node: ReactNode, prefix: string): ReactNode {
  return Children.map(node, (child) => {
    if (!isValidElement(child)) return child;
    const props = child.props as Record<string, unknown>;

    // AntD Form.Item with a name prop → try to inject testid on its child.
    if (child.type === Form.Item && props["name"] != null) {
      const inner = props["children"];
      if (
        isValidElement(inner) &&
        !(inner.props as Record<string, unknown>)["data-testid"]
      ) {
        const rawName = props["name"];
        const fieldName = Array.isArray(rawName)
          ? rawName.join(".")
          : typeof rawName === "string"
          ? rawName
          : String(rawName as string | number);
        const clonedInner = cloneElement(
          inner,
          { "data-testid": `${prefix}-${fieldName}` } as Record<string, unknown>,
        );
        return cloneElement(
          child as React.ReactElement,
          {} as Record<string, unknown>,
          clonedInner,
        );
      }
      return child;
    }

    // Recurse into other elements that carry children.
    if (props["children"]) {
      return cloneElement(
        child as React.ReactElement,
        {} as Record<string, unknown>,
        injectFormItemTestIds(props["children"] as ReactNode, prefix),
      );
    }

    return child;
  });
}

// ─── Component ────────────────────────────────────────────────────────────

export function CrudModalShell({
  open,
  mode,
  entityName,
  title,
  loading = false,
  saving = false,
  error,
  children,
  width = 720,
  saveLabel,
  cancelLabel = "Anuluj",
  saveDisabled = false,
  testId = "crud-modal",
  formTestIdPrefix,
  onClose,
  onSubmit,
}: CrudModalShellProps) {
  const resolvedTitle = title ?? defaultCrudTitle(mode, entityName);
  const resolvedSaveLabel = saveLabel ?? defaultCrudSaveLabel(mode);

  const banner = error ? <ErrorBanner message={error} /> : undefined;

  const wrappedBody = formTestIdPrefix ? (
    <FormFieldTestIdContext.Provider value={formTestIdPrefix}>
      {injectFormItemTestIds(children, formTestIdPrefix)}
    </FormFieldTestIdContext.Provider>
  ) : children;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={resolvedTitle}
      width={width}
      testId={testId}
      banner={banner}
      body={wrappedBody}
      actions={[
        {
          id:        "cancel",
          label:     cancelLabel,
          variant:   "default",
          disabled:  saving,
          onTrigger: onClose,
        },
        {
          id:        "save",
          label:     resolvedSaveLabel,
          variant:   "primary",
          disabled:  loading || saveDisabled,
          loading:   saving,
          onTrigger: onSubmit,
        },
      ]}
    />
  );
}

// ─── ErrorBanner — internal helper ───────────────────────────────────────
//
// Tonal red banner row sitting between header and body. Renders inside
// Modal's `banner` slot which already handles the negative-margin /
// flush-edge geometry — we only paint the row.

function ErrorBanner({ message }: { message: string }) {
  return (
    <Box
      testId="crud-modal-error"
      tone="red"
      paddingX="xl"
      paddingY="sm"
      divider="bottom"
    >
      <TextValue size="sm" weight="medium">
        {message}
      </TextValue>
    </Box>
  );
}

